const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const MAX_DEPTH = 50;

function isPlainObject(item: unknown): boolean {
  if (item === null || typeof item !== 'object') {
    return false;
  }
  if (Array.isArray(item)) {
    return false;
  }
  const proto = Object.getPrototypeOf(item);
  return proto === Object.prototype || proto === null;
}

function deepMergeInner(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  stack: object[],
  depth: number,
): void {
  if (depth > MAX_DEPTH) {
    return;
  }
  if (stack.includes(source)) {
    return;
  }
  stack.push(source);
  try {
    for (const key of Object.keys(source)) {
      if (DANGEROUS_KEYS.has(key)) {
        continue;
      }
      const srcVal = source[key];

      if (isPlainObject(srcVal)) {
        const tgtVal = target[key];
        if (isPlainObject(tgtVal)) {
          deepMergeInner(
            tgtVal as Record<string, unknown>,
            srcVal as Record<string, unknown>,
            stack,
            depth + 1,
          );
        } else {
          target[key] = {};
          deepMergeInner(
            target[key] as Record<string, unknown>,
            srcVal as Record<string, unknown>,
            stack,
            depth + 1,
          );
        }
      } else {
        target[key] = srcVal;
      }
    }
  } finally {
    stack.pop();
  }
}

/**
 * Recursively merges plain-object properties from `source` into `target` (mutates `target`).
 * Arrays, `Date`, `RegExp`, and non-plain objects are replaced, not merged.
 * Ignores `__proto__`, `constructor`, and `prototype` keys on `source` to avoid prototype pollution.
 */
export default function deepMerge(target: unknown, source: unknown): unknown {
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return source;
  }
  deepMergeInner(target, source, [], 0);
  return target;
}
